import { ArrowRight, Check, ShieldCheck } from "lucide-react";
import Logo from "@/components/Logo";
import useSEO from "@/lib/useSEO";

/**
 * Reusable partner benefit landing template.
 * Every visible piece of content comes from the `partner` prop — clone
 * a partner entry in `lib/partners.js` to spin up a new landing page.
 *
 * Layout:
 *   [Co-branded header]  Melbi  ×  Partner
 *   [Headline]           Your <Partner> Wellness Benefit
 *   [Intro paragraph]
 *   [Treatment categories]
 *   [Benefit code block]
 *   [Claim CTA]
 *   [Trust line + fine print]
 */
export default function PartnerLanding({ partner }) {
  useSEO({
    title: `Your ${partner.name} Wellness Benefit | Melbi Wellness`,
    description: `Melbi Wellness is an exclusive employee benefit for ${partner.name} team members. Clinician-guided care, licensed U.S. pharmacy fulfillment, delivered online.`,
    url: `https://melbiwellness.com/partners/${partner.slug}`,
  });

  const PartnerMark = () =>
    partner.logo.image ? (
      <img
        src={partner.logo.image}
        alt={partner.logo.alt || `${partner.name} logo`}
        className="h-8 md:h-10 w-auto object-contain"
        data-testid={`partner-logo-${partner.slug}`}
      />
    ) : (
      <span
        data-testid={`partner-wordmark-${partner.slug}`}
        className="font-display text-2xl md:text-[1.75rem] font-bold tracking-tight leading-none"
        style={{ color: partner.logo.accent || "#2C2B29" }}
      >
        {partner.logo.text}
        <span className="ml-0.5" style={{ color: "#C96A4E" }}>.</span>
      </span>
    );

  return (
    <div
      data-testid={`partner-landing-${partner.slug}`}
      className="min-h-screen bg-[#FAF6EF]"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 md:pt-16 pb-12 md:pb-20">
        {/* CO-BRANDED HEADER */}
        <div
          data-testid="partner-cobranded-header"
          className="flex items-center justify-center gap-4 md:gap-6 flex-wrap"
        >
          <Logo />
          <span
            aria-hidden="true"
            className="text-[#8A9A92] font-display text-xl md:text-2xl leading-none select-none"
          >
            ×
          </span>
          <PartnerMark />
        </div>

        <p className="mt-8 md:mt-12 text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A92] text-center">
          Exclusive employee benefit
        </p>

        {/* HEADLINE */}
        <h1
          data-testid="partner-headline"
          className="mt-3 font-display text-[2rem] sm:text-4xl md:text-5xl font-bold tracking-tight text-[#2C2B29] leading-[1.1] text-center"
        >
          Your {partner.shortName || partner.name}{" "}
          <span className="italic text-[#C96A4E]">Wellness Benefit</span>
        </h1>

        {/* INTRO */}
        <p
          data-testid="partner-intro"
          className="mt-6 md:mt-8 text-[#5E5C58] text-base md:text-lg leading-relaxed text-center max-w-2xl mx-auto"
        >
          {partner.intro}
        </p>

        {/* CATEGORIES */}
        <div className="mt-10 md:mt-14">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A92] text-center">
            What's included
          </p>
          <ul
            data-testid="partner-categories"
            className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 max-w-xl mx-auto text-[#2C2B29]"
          >
            {partner.categories.map((cat) => (
              <li
                key={cat}
                className="flex items-start gap-2.5 text-sm md:text-base"
              >
                <span className="mt-0.5 w-5 h-5 rounded-full bg-white border border-[#E8E5DF] flex items-center justify-center shrink-0">
                  <Check size={13} className="text-[#C96A4E]" />
                </span>
                <span className="leading-snug">{cat}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* BENEFIT CODE */}
        <div
          data-testid="partner-code-block"
          className="mt-10 md:mt-14 rounded-2xl border border-[#E8E5DF] bg-white px-5 py-6 md:px-8 md:py-7 text-center shadow-[0_4px_24px_rgba(0,0,0,0.03)]"
        >
          <p className="text-[11px] md:text-xs font-bold uppercase tracking-[0.22em] text-[#8A9A92]">
            Your benefit code
          </p>
          <p
            data-testid="partner-code"
            className="mt-3 font-display text-3xl md:text-4xl font-bold tracking-[0.14em] text-[#2C2B29] leading-none"
          >
            {partner.code}
          </p>
          <p className="mt-3 text-xs md:text-sm text-[#5E5C58] leading-relaxed max-w-md mx-auto">
            Enter this code during checkout to unlock your {partner.shortName || partner.name} partner rate.
          </p>
        </div>

        {/* CTA */}
        <div className="mt-8 md:mt-10 flex justify-center">
          <a
            href={partner.claimUrl}
            data-testid="partner-claim-cta"
            className="inline-flex items-center justify-center bg-[#C96A4E] text-white rounded-full px-8 py-3 md:py-4 text-base md:text-lg font-semibold hover:bg-[#B35A40] transition-colors shadow-sm"
          >
            Claim Your Benefit
            <ArrowRight size={18} className="ml-2" />
          </a>
        </div>

        {/* TRUST LINE */}
        <div className="mt-8 md:mt-10 flex items-center justify-center gap-2 text-xs md:text-sm text-[#5E5C58]">
          <ShieldCheck size={14} className="text-[#8A9A92]" />
          <span data-testid="partner-trust-line">
            Licensed U.S. pharmacy fulfillment.
          </span>
        </div>

        {/* FINE PRINT */}
        <p className="mt-6 md:mt-8 text-[11px] md:text-xs text-[#8A9A92] leading-relaxed text-center max-w-xl mx-auto">
          Not all patients qualify. Clinical review is required. Prescriptions
          are issued only when medically appropriate. Compounded medications are
          not reviewed by the FDA for safety, effectiveness, or quality.
        </p>
      </div>
    </div>
  );
}
